import React, { useState, useEffect, useRef } from 'react';

/**
 * Account Actions Menu Component
 * Dropdown menu for user account actions (edit, subscription, deactivate, delete)
 */
const AccountActionsMenu = ({ userItem, userTab, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const handleAction = (action) => {
    setIsOpen(false);
    onAction(action, userItem);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef} data-testid="account-actions-menu">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="focus:outline-none p-2 rounded-full transition-colors duration-200"
        style={{color: '#8A92B2'}}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#00C2FF';
          e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#8A92B2';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        type="button"
        title="Actions"
        data-testid="account-actions-trigger"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-56 rounded-md shadow-lg z-50 border py-1"
          style={{
            backgroundColor: '#242A3D',
            borderColor: '#1C2133'
          }}
          data-testid="account-actions-dropdown"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction('edit');
            }}
            className="flex items-center w-full text-left px-4 py-2 text-sm transition-colors duration-150"
            style={{color: '#FFFFFF'}}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(74, 91, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            type="button"
            data-testid="account-action-edit"
          >
            <span className="mr-3">✏️</span>
            Éditer l&apos;utilisateur
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction('subscription');
            }}
            className="flex items-center w-full text-left px-4 py-2 text-sm transition-colors duration-150"
            style={{color: '#FFFFFF'}}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(74, 91, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            type="button"
            data-testid="account-action-subscription"
          >
            <span className="mr-3">📅</span>
            Gérer l&apos;abonnement
          </button>
          
          <hr className="my-1" style={{borderColor: '#1C2133'}} />
          
          {userTab === 'active' && userItem.role !== 'admin' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAction('deactivate');
              }}
              className="flex items-center w-full text-left px-4 py-2 text-sm transition-colors duration-150"
              style={{color: '#FF5757'}}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              type="button"
              data-testid="account-action-deactivate"
            >
              <span className="mr-3">🚫</span>
              Désactiver le compte
            </button>
          )}
          
          {userItem.role !== 'admin' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAction('delete_permanent');
              }}
              className="flex items-center w-full text-left px-4 py-2 text-sm transition-colors duration-150"
              style={{color: '#FF5757'}}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              type="button"
              data-testid="account-action-delete"
            >
              <span className="mr-3">🗑️</span>
              Supprimer définitivement
            </button>
          )}
          
          {userTab === 'inactive' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAction('activate');
              }}
              className="flex items-center w-full text-left px-4 py-2 text-sm transition-colors duration-150"
              style={{color: '#00C2FF'}}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              type="button"
              data-testid="account-action-activate"
            >
              <span className="mr-3">✅</span>
              Réactiver le compte
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountActionsMenu;
